import { m as motion, useScroll } from 'framer-motion';
import { GetStaticProps, InferGetStaticPropsType } from 'next';
import { useRef } from 'react';
import type { Thing } from 'schema-dts';
import { MAIN_FORUM_EDITION_YEAR, PAGES } from '@/consts';
import { TeamPerson } from '@/entities';
import { Page } from '@/entities/page';
import { AppCompaniesResponse, getCommonPageProps, getCompanies, getCompanyCategories } from '@/shared/api';
import { getEmployees } from '@/shared/api/employees';
import { getHistory } from '@/shared/api/history';
import { getPageDetail } from '@/shared/api/page-detail';
import { getPages } from '@/shared/api/pages';
import { getPeople } from '@/shared/api/people';
import { postParticipantsList } from '@/shared/api/post-participants-list';
import { getPrivileges } from '@/shared/api/privileges';
import { getQuestions } from '@/shared/api/questions';
import { getSiteSettings } from '@/shared/api/site-settings';
import { getTicketFormats } from '@/shared/api/ticket-formats';
import { getTickets } from '@/shared/api/tickets';
import { useHeaderAnchorsEffect } from '@/shared/atoms/header-anchors';
import { PageDataState } from '@/shared/atoms/page-data';
import DefaultLayout from '@/shared/components/layout/DefaultLayout';
import ExpoHeroSection, { ExpoHeroSectionRawProps } from '@/shared/components/sections/ExpoHeroSection';
import FaqSection, { FaqSectionProps } from '@/shared/components/sections/FaqSection';
import FormatsSection, { FormatsSectionRawProps } from '@/shared/components/sections/FormatsSection';
import HowItWasSection, { HowItWasSectionProps } from '@/shared/components/sections/HowItWasSection';
import ParticipantsListSection from '@/shared/components/sections/ParticipantsListSection';
import QuestionsSection, { QuestionsSectionProps } from '@/shared/components/sections/QuestionsSection';
import ServicesSection, { ServicesSectionProps } from '@/shared/components/sections/ServicesSection';
import SpeakersGridSection from '@/shared/components/sections/SpeakersGridSection';
import { SpeakersSectionRawProps } from '@/shared/components/sections/SpeakersSection';
import {
    TariffsInfoSectionWithPopup,
    TariffsInfoSectionWithPopupProps,
} from '@/shared/components/sections/TariffsInfoSection';
import YearParticipantsSection, {
    YearParticipantsSectionRawProps,
} from '@/shared/components/sections/YearParticipantsSection';
import Breadcrumbs from '@/shared/components/shared/Breadcrumbs';
import JsonLd from '@/shared/components/shared/JsonLd/JsonLd';
import { ParticipantsListFormRawProps } from '@/shared/components/shared/ParticipantsListForm';
import { PARTICIPATION_NAVIGATION } from '@/shared/consts/participation-nav';
import { Anchors, AppNavigation, CommonPageProps, NonUndefined } from '@/shared/lib/types';
import { generateBusinessEventJsonLd } from '@/shared/lib/utils/business-event-json-ld';
import { ddMmYyyyToIso8601Date, parseEventDates } from '@/shared/lib/utils/dates';
import { filterFaqQuestionsVisibleInFaqSection } from '@/shared/lib/utils/faq-json-ld';
import { buildTariffContacts } from '@/shared/lib/utils/tariff-contacts';

const PARTICIPANTS_SECTION_IDS = {
    formats: 'formats',
    speakers: 'speakers',
    tarifs: 'tarifs',
    participantsList: 'participantsList',
    partners: 'partners',
    howItWas: 'howItWas',
    services: 'services',
    faq: 'faq',
    feedback: 'feedback',
} as const;

const ParticipationPage = ({
    pageData,
    breadcrumbs,
    bodyClass,
    navigation,
    expoHeroSectionProps,
    formatsSectionProps,
    speakersSectionProps,
    tariffsInfoSectionProps,
    participantsListSectionProps,
    yearParticipantsSectionProps,
    howItWasSectionProps,
    servicesSectionProps,
    faqSectionProps,
    questionsSectionProps,
    contactPersonProps,
    anchors = [],
    sectionIds,
    footerData,
    meta,
    h1,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ['start -30%', 'end end'] });

    useHeaderAnchorsEffect({ anchors, navigation, scrollYProgress });

    const eventDates = expoHeroSectionProps.date
        ? parseEventDates(expoHeroSectionProps.date, MAIN_FORUM_EDITION_YEAR)
        : null;

    return (
        <DefaultLayout currentPageId={PAGES.PARTICIPANTS} ref={ref} bodyClass={bodyClass}>
            <script
                type="application/json"
                className="js-page-data-json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(pageData) }}
            ></script>

            <section className="visitors__section">
                <Breadcrumbs className="visually-hidden" breadcrumbs={breadcrumbs} />
                <ExpoHeroSection id="top" expoHeroSectionProps={expoHeroSectionProps} />

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1 }}
                    exit={{ opacity: 0 }}
                >
                    {(tariffsInfoSectionProps.tariffs?.length ?? 0) > 0 && (
                        <TariffsInfoSectionWithPopup {...tariffsInfoSectionProps} id={sectionIds.tarifs} />
                    )}

                    {formatsSectionProps.items && formatsSectionProps.items.length > 0 && (
                        <FormatsSection {...formatsSectionProps} id={sectionIds.formats} />
                    )}

                    <ParticipantsListSection
                        id={sectionIds.participantsList}
                        participantsListSectionProps={{
                            ...participantsListSectionProps,
                            submitFn: postParticipantsList,
                        }}
                    />

                    {(speakersSectionProps.speakers?.length ?? 0) > 0 && (
                        <SpeakersGridSection id={sectionIds.speakers} speakersSectionProps={speakersSectionProps} />
                    )}

                    {(yearParticipantsSectionProps.items?.length ?? 0) > 0 && (
                        <YearParticipantsSection
                            id={sectionIds.partners}
                            yearParticipantsSectionProps={yearParticipantsSectionProps}
                        />
                    )}

                    <ServicesSection
                        id={sectionIds.services}
                        servicesSectionProps={servicesSectionProps}
                        className="js-header-theme-trigger"
                        data-theme="dark"
                    />

                    {howItWasSectionProps.slider.images.length > 0 && (
                        <HowItWasSection
                            id={sectionIds.howItWas}
                            howItWasSectionProps={howItWasSectionProps}
                            theme="dark"
                        />
                    )}
                    {filterFaqQuestionsVisibleInFaqSection(faqSectionProps.questions).length > 0 && (
                        <FaqSection id={sectionIds.faq} faqSectionProps={faqSectionProps} />
                    )}

                    <QuestionsSection
                        id={sectionIds.feedback}
                        questionsSectionProps={questionsSectionProps}
                        contactPersonProps={contactPersonProps}
                    />
                </motion.div>
            </section>
            <JsonLd<Thing>
                inBody
                scriptKey="ld-json-participants"
                data={generateBusinessEventJsonLd({
                    variant: 'forum',
                    name: expoHeroSectionProps.title || h1,
                    description: meta.description,
                    disambiguatingDescription: formatsSectionProps.text,
                    startDateIso: eventDates?.startDay ? ddMmYyyyToIso8601Date(eventDates.startDay) : null,
                    endDateIso: eventDates?.endDay ? ddMmYyyyToIso8601Date(eventDates.endDay) : null,
                    imageSrc: meta.ogImage,
                    offers: (tariffsInfoSectionProps.tariffs ?? []).map((tariff) => ({
                        name: tariff.title,
                        price: tariff.price,
                        url: tariff.button?.href,
                    })),
                    breadcrumbs,
                    faqQuestions: faqSectionProps.questions,
                    contacts: footerData,
                })}
            />
        </DefaultLayout>
    );
};

export default ParticipationPage;

type ParticipationPageProps = NonUndefined<
    CommonPageProps & {
        page?: Page;
        h1: string;
        pageData: PageDataState;
        expoHeroSectionProps: ExpoHeroSectionRawProps;
        formatsSectionProps: FormatsSectionRawProps;
        speakersSectionProps: SpeakersSectionRawProps;
        tariffsInfoSectionProps: TariffsInfoSectionWithPopupProps;
        participantsListSectionProps: ParticipantsListFormRawProps;
        yearParticipantsSectionProps: YearParticipantsSectionRawProps;
        howItWasSectionProps: HowItWasSectionProps;
        servicesSectionProps: ServicesSectionProps;
        faqSectionProps: FaqSectionProps;
        questionsSectionProps: QuestionsSectionProps;
        contactPersonProps: TeamPerson | null;
        navigation: AppNavigation;
        anchors?: Anchors;
        sectionIds: typeof PARTICIPANTS_SECTION_IDS;
    }
>;

export const getStaticProps: GetStaticProps<ParticipationPageProps> = async () => {
    const pageResponse = await getPageDetail({ params: { slug: 'participants' } });
    const servicePageResponse = await getPageDetail({ params: { slug: 'services' } });
    const pageId = pageResponse.data?.id ?? PAGES.PARTICIPANTS;

    const [
        commonPageProps,
        historyResponse,
        questionsResponse,
        servicesResponse,
        employeesResponse,
        peopleResponse,
        privilegesResponse,
        ticketsResponse,
        companiesResponse,
        siteSettings,
    ] = await Promise.all([
        getCommonPageProps(pageId),
        getHistory({ params: { page: pageId } }),
        getQuestions({ params: { page: pageId } }),
        getPages({ params: { parent_id: servicePageResponse.data?.id ?? PAGES.SERVICES } }),
        getEmployees({ params: { page: pageId } }),
        getPeople({ params: { page: pageId } }),
        getPrivileges({ params: { page: pageId } }),
        getTickets({ params: { page: pageId } }),
        getCompanyCategories({ params: { page: pageId } }).then((categoriesResponse) => {
            const categories = categoriesResponse.data ?? [];

            if (categories.length === 0) {
                return Promise.resolve<AppCompaniesResponse>({
                    status: 'success',
                    data: [],
                });
            }

            return getCompanies({
                params: { categories: categories.map((category) => category.id) },
            });
        }),
        getSiteSettings({
            params: {
                keys: [
                    'participants_first_screen_title',
                    'participants_first_screen_caption',
                    'participants_first_screen_date',
                    'participants_first_screen_place',
                    'participants_first_screen_program_button_text',
                    'participants_first_screen_participate_button_text',
                    'participants_first_screen_partners_title',
                    'participants_first_screen_partner_images',
                    'participants_first_screen_bg_image',
                    'participants_first_screen_bg_video',

                    'participants_tariff_title',
                    'participants_tariff_popup_title',
                    'participants_tariff_description',
                    'participants_tariff_button_text',
                    'participants_tariff_document',
                    'participants_tariff_disclaimer',
                    'participants_tariff_discount_price_label',
                    'participants_tariff_future_price_label',
                    'participants_tariff_contact_title',
                    'participants_tariff_contact_description',
                    'participants_tariff_contact_phone',
                    'participants_tariff_contact_email',
                    'participants_tariff_footnote_description',

                    'participants_key_formats_title',
                    'participants_key_formats_description',
                    'participants_key_formats_button_text',
                    'participants_key_formats_tickets_caption',

                    'participants_participants_list_form_title',
                    'participants_participants_list_form_description',
                    'participants_participants_list_form_partner_images',

                    'participants_people_title',
                    'participants_people_description',

                    'participants_partners_title',
                    'participants_partners_description',

                    'participants_service_title',
                    'participants_service_description',
                    'participants_service_link_text',
                    'participants_service_link',

                    'participants_faq_title',

                    'participants_history_title',
                    'participants_history_description',
                    'participants_history_link_text',
                    'participants_history_link',

                    'participants_form_title',
                    'participants_form_description',
                    'participants_form_link_text',
                    'participants_form_link',
                ],
            },
        }),
    ]);

    const tickets = ticketsResponse.data ?? [];
    const ticketFormatsResponse =
        tickets.length > 0 ? await getTicketFormats({ params: { tickets: tickets.map((ticket) => ticket.id) } }) : null;
    const ticketFormats = ticketFormatsResponse?.data ?? [];
    const speakers = peopleResponse.data ?? [];

    const formatsSectionProps = {
        title: siteSettings.data?.participants_key_formats_title ?? '',
        text: siteSettings.data?.participants_key_formats_description ?? '',
        buttonText: siteSettings.data?.participants_key_formats_button_text ?? 'Участвовать',
        buttonHref: `/program/`,
        tabsLabel: siteSettings.data?.participants_key_formats_tickets_caption ?? '',
        items: ticketFormats.map((format) => ({
            id: String(format.id),
            title: format.name,
            text: format.description ?? '',
            href: format.url || null,
        })),
        tabs: tickets.map((ticket) => ({
            id: String(ticket.id),
            label: ticket.title,
            activeItemIds: ticketFormats
                .filter((format) => format.ticketIds.includes(ticket.id))
                .map((format) => String(format.id)),
        })),
    } satisfies FormatsSectionRawProps;

    const tariffDisclaimer = siteSettings.data?.participants_tariff_disclaimer ?? '';
    const tariffButtonText = siteSettings.data?.participants_tariff_button_text ?? '';
    const tariffDocumentSrc = siteSettings.data?.participants_tariff_document?.src;

    const tariffsInfoSectionProps = {
        title: siteSettings.data?.participants_tariff_title ?? '',
        subtitle: siteSettings.data?.participants_tariff_description ?? '',
        download: tariffDocumentSrc
            ? {
                  href: tariffDocumentSrc,
                  text: tariffButtonText || 'Скачать тарифы',
              }
            : null,
        popupTitle: siteSettings.data?.participants_tariff_popup_title ?? '',
        allTariffFeatures: privilegesResponse.data ?? [],
        tariffs: tickets.map((ticket) => ({
            id: ticket.id,
            title: ticket.title,
            text: ticket.description ?? '',
            color: ticket.color || null,
            price: ticket.price ?? '',
            priceNote: tariffDisclaimer || null,
            button: {
                text: ticket.button?.text || null,
                href: ticket.button?.href || '/registration/',
                isExternal: ticket.button?.isExternal || false,
            },
            features: ticket.features ?? [],
            futurePrice: ticket.futurePrice ?? '',
        })),
        discounts: tickets
            .filter((ticket) => ticket.price)
            .map((ticket) => ({
                tariffId: ticket.id,
                price: ticket.price ?? '',
            })),
        discountPriceLabel: siteSettings.data?.participants_tariff_discount_price_label || null,
        futurePriceLabel: siteSettings.data?.participants_tariff_future_price_label || null,
        footnotes: siteSettings.data?.participants_tariff_footnote_description ?? null,
        notes: ['* Кроме мероприятий по приглашениям'],
        contacts: buildTariffContacts({
            title: siteSettings.data?.participants_tariff_contact_title,
            description: siteSettings.data?.participants_tariff_contact_description,
            phone: siteSettings.data?.participants_tariff_contact_phone,
            email: siteSettings.data?.participants_tariff_contact_email,
        }),
    } satisfies TariffsInfoSectionWithPopupProps;

    const bgVideo = siteSettings.data?.participants_first_screen_bg_video;
    const heroMedia =
        bgVideo && bgVideo.length > 0 ? bgVideo : (siteSettings.data?.participants_first_screen_bg_image ?? null);

    return {
        props: {
            ...commonPageProps,
            bodyClass: 'expo-page participants-page',
            meta: {
                ...commonPageProps.meta,
                ...(pageResponse.data?.meta ?? []),
            },
            breadcrumbs: [
                { text: 'TNF', href: '/' },
                { text: 'FORUM', href: '/participation/participants/' },
            ],
            pageData: {
                pageId: pageResponse.data?.id ?? -1,
            },
            page: pageResponse.data,
            h1: pageResponse.data?.name ?? 'Участник FORUM',
            navigation: PARTICIPATION_NAVIGATION,
            sectionIds: PARTICIPANTS_SECTION_IDS,
            expoHeroSectionProps: {
                media: heroMedia,
                title: siteSettings.data?.participants_first_screen_title ?? '',
                text: siteSettings.data?.participants_first_screen_caption ?? '',
                date: siteSettings.data?.participants_first_screen_date ?? '',
                location: siteSettings.data?.participants_first_screen_place ?? '',
                programButtonText: siteSettings.data?.participants_first_screen_program_button_text ?? '',
                participateButtonText: siteSettings.data?.participants_first_screen_participate_button_text ?? '',
                participateButtonHref: `#${PARTICIPANTS_SECTION_IDS.tarifs}`,
                partnersTitle: siteSettings.data?.participants_first_screen_partners_title ?? '',
                partners: siteSettings.data?.participants_first_screen_partner_images ?? [],
            } satisfies ExpoHeroSectionRawProps,
            formatsSectionProps,
            speakersSectionProps: {
                title: siteSettings.data?.participants_people_title ?? '',
                text: siteSettings.data?.participants_people_description ?? '',
                speakers,
            } satisfies SpeakersSectionRawProps,
            tariffsInfoSectionProps,
            participantsListSectionProps: {
                title: siteSettings.data?.participants_participants_list_form_title || '',
                description: siteSettings.data?.participants_participants_list_form_description || '',
                video: [{ src: '/video/output-opt.mp4', type: 'video/mp4' }],
                poster: {
                    src: '/video/output-opt-poster.jpg',
                    width: 1920,
                    height: 1080,
                },
                logos: siteSettings.data?.participants_participants_list_form_partner_images || [],
            },
            yearParticipantsSectionProps: {
                title: siteSettings.data?.participants_partners_title ?? '',
                text: siteSettings.data?.participants_partners_description ?? '',
                items: (companiesResponse.data ?? []).map((company) => ({
                    logo: company.logo || company.mainLogo || null,
                    title: company.title ?? '',
                    href: company.href || null,
                })),
                button: {
                    text: 'Все партнеры',
                    href: '/participation/partners/',
                },
            } satisfies YearParticipantsSectionRawProps,
            howItWasSectionProps: {
                slider: historyResponse.data ?? { images: [] },
                title: siteSettings.data?.participants_history_title ?? '',
                description: siteSettings.data?.participants_history_description ?? '',
                link: siteSettings.data?.participants_history_link
                    ? {
                          href: siteSettings.data?.participants_history_link,
                          name: siteSettings.data?.participants_history_link_text ?? '',
                      }
                    : null,
            },
            faqSectionProps: {
                title: siteSettings.data?.participants_faq_title ?? '',
                questions: questionsResponse.data ?? [],
            },
            questionsSectionProps: {
                title: siteSettings.data?.participants_form_title ?? '',
                description: siteSettings.data?.participants_form_description ?? '',
                linkText: siteSettings.data?.participants_form_link_text ?? '',
                href: siteSettings.data?.participants_form_link ?? '',
            },
            contactPersonProps: employeesResponse.data?.[0] || null,
            servicesSectionProps: {
                title: siteSettings.data?.participants_service_title ?? '',
                subtitle: siteSettings.data?.participants_service_description ?? '',
                cards: servicesResponse.data ?? [],
            },
        } satisfies ParticipationPageProps,
        revalidate: 60,
    };
};
